import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Filter, RotateCcw } from "lucide-react";
import { clsx } from "clsx";
import { propertiesService } from "@/services/propertiesService";
import { usersService } from "@/services/usersService";
import type { PropertySummary, UserSummary } from "@/types/entities";
import { CalendarCheckboxList } from "./CalendarCheckboxList";
import { DiaryDateField } from "./DiaryDateTimeField";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import {
  diaryCalendarFiltersDefault,
  applyDiaryCalendarFilterPatch,
  isDefaultDiaryCalendarFilters,
  type DiaryCalendarFilterState,
} from "./diaryEntryUtils";

interface Props {
  legalEntityId?: string;
  branchId?: string;
  currentUser: UserSummary | null;
  isAdmin: boolean;
  /** When false (negotiator), hide the "All Events" toggle. */
  showAllEventsFilter?: boolean;
  value: DiaryCalendarFilterState;
  onChange: (next: DiaryCalendarFilterState) => void;
}

function ToggleRow({ checked, label, onClick }: { checked: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
        checked ? "bg-bridge-accent/10 text-bridge-accent font-medium" : "text-bridge-fg hover:bg-bridge-bg",
      )}
    >
      <span
        className={clsx(
          "flex items-center justify-center w-4 h-4 rounded border shrink-0",
          checked ? "bg-bridge-accent border-bridge-accent text-white" : "border-bridge-border/70",
        )}
      >
        {checked && <Check size={12} strokeWidth={3} />}
      </span>
      <span className="truncate flex-1">{label}</span>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider text-bridge-subtle">
      {children}
    </div>
  );
}

/** Google Calendar-style "which calendars are visible" filter for the Diary page. */
export function DiaryCalendarFilters({
  legalEntityId,
  branchId,
  currentUser,
  isAdmin,
  showAllEventsFilter = true,
  value,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const openSnapshotRef = useRef(value);
  // id→label cache shared by all checkbox lists so selected filters keep their
  // labels (and stay pinned at the top) across dropdown close/reopen, searches
  // and pagination. Survives because this component stays mounted on the page.
  const labelCacheRef = useRef(new Map<string, string>());

  useEffect(() => {
    if (open) openSnapshotRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-floating-surface]")) return;
      if (rootRef.current && !rootRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const [hovered, setHovered] = useState(false);

  const isNegotiator = !showAllEventsFilter;
  const isDefault = isDefaultDiaryCalendarFilters(value, isNegotiator);
  const activeCount =
    value.userIds.length +
    value.propertyIds.length +
    value.availabilityPropertyIds.length +
    (value.date ? 1 : 0) +
    (value.createdBy ? 1 : 0) +
    (value.includeMe ? 1 : 0) +
    (showAllEventsFilter && value.includeAllEvents ? 1 : 0);

  const tooltipLines = useMemo(() => {
    const lines: string[] = [];
    if (showAllEventsFilter && value.includeAllEvents) lines.push("All Events");
    if (value.includeMe) lines.push("Me");
    if (value.userIds.length > 0)
      lines.push(`${value.userIds.length} team member${value.userIds.length > 1 ? "s" : ""}`);
    if (value.propertyIds.length > 0)
      lines.push(`${value.propertyIds.length} propert${value.propertyIds.length > 1 ? "ies" : "y"}`);
    if (value.availabilityPropertyIds.length > 0)
      lines.push(`${value.availabilityPropertyIds.length} availability calendar${value.availabilityPropertyIds.length > 1 ? "s" : ""}`);
    if (value.date) lines.push(`Date: ${value.date}`);
    if (value.createdBy) lines.push(`Created by: ${value.createdBy === "BRIDGE_AI" ? "BridgeAI" : "CRM"}`);
    return lines;
  }, [showAllEventsFilter, value]);

  const showTooltip = hovered && !open && tooltipLines.length > 0;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={clsx(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors",
          !isDefault
            ? "border-bridge-accent/60 bg-bridge-accent/10 text-bridge-accent"
            : "border-bridge-border/60 bg-bridge-surface/60 text-bridge-muted hover:text-bridge-fg",
        )}
      >
        <Filter size={14} className="shrink-0" />
        <span>Filters</span>
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-bridge-accent text-white text-[10px] font-bold">
            {activeCount}
          </span>
        )}
        <ChevronDown size={14} className={clsx("shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {showTooltip && (
        <div className="absolute right-full top-0 mr-2 z-50 pointer-events-none">
          <div className="relative bg-ink-800 text-white text-xs rounded px-3 py-2 shadow-lg whitespace-nowrap">
            <div className="absolute -right-1 top-2 w-2 h-2 bg-ink-800 rotate-45" />
            <div className="font-semibold mb-1">Active Filters</div>
            {tooltipLines.map((line) => (
              <div key={line} className="py-0.5">• {line}</div>
            ))}
          </div>
        </div>
      )}

      {open && (
        <div className="absolute right-0 z-50 mt-2 flex max-h-[calc(100vh-120px)] w-[40rem] max-w-[calc(100vw-2rem)] flex-col divide-y divide-bridge-border/40 overflow-y-auto rounded-xl border border-bridge-border/70 bg-white shadow-lg shadow-slate-200/80 scrollbar-metal">
          <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="flex flex-col divide-y divide-bridge-border/40">
          <div className="pb-3">
            <SectionLabel>Event filters</SectionLabel>
            <div className="space-y-2 px-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-bridge-muted" htmlFor="diary-filter-date">
                  Date
                </label>
                <DiaryDateField
                  id="diary-filter-date"
                  value={value.date}
                  onChange={(date) => onChange({ ...value, date })}
                  placeholder="Any date"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-bridge-muted">Created by</label>
                <MenuSelect
                  hideLabel
                  value={value.createdBy}
                  onChange={(createdBy) => onChange({
                    ...value,
                    createdBy: createdBy as DiaryCalendarFilterState["createdBy"],
                  })}
                  options={[
                    { value: "", label: "BridgeAI or CRM" },
                    { value: "BRIDGE_AI", label: "BridgeAI" },
                    { value: "CRM", label: "CRM" },
                  ]}
                  placeholder="BridgeAI or CRM"
                />
              </div>
            </div>
          </div>

          <div className="pb-1">
            <SectionLabel>My Diary</SectionLabel>
            {(() => {
              const allEventsRow = showAllEventsFilter ? (
                <ToggleRow
                  key="allEvents"
                  checked={value.includeAllEvents}
                  label="All Events"
                  onClick={() => onChange({ ...value, includeAllEvents: !value.includeAllEvents })}
                />
              ) : null;
              const meRow = currentUser ? (
                <ToggleRow
                  key="me"
                  checked={value.includeMe}
                  label={`Me — ${currentUser.name || currentUser.email || "My calendar"}`}
                  onClick={() => onChange({ ...value, includeMe: !value.includeMe })}
                />
              ) : null;

              const rows: { key: string; wasSelected: boolean; element: React.ReactNode }[] = [];
              if (allEventsRow) {
                rows.push({
                  key: "allEvents",
                  wasSelected: openSnapshotRef.current.includeAllEvents,
                  element: allEventsRow,
                });
              }
              if (meRow) rows.push({ key: "me", wasSelected: openSnapshotRef.current.includeMe, element: meRow });
              rows.sort((a, b) => {
                if (a.wasSelected === b.wasSelected) return 0;
                return a.wasSelected ? -1 : 1;
              });
              return rows.map((r) => r.element);
            })()}
          </div>

          {isAdmin && (
            <div className="pb-1">
              <SectionLabel>Team Members</SectionLabel>
              <CalendarCheckboxList
                selectedIds={value.userIds}
                onChange={(ids) => onChange(applyDiaryCalendarFilterPatch(value, { userIds: ids }))}
                getId={(u: UserSummary) => u.id}
                getLabel={(u: UserSummary) => u.name || u.email || u.id}
                labelCache={labelCacheRef.current}
                searchPlaceholder="Search team members..."
                emptyLabel="No team members found"
                loadPage={(page, search) =>
                  usersService
                    .list(
                      { legalEntityId, search: search || undefined },
                      { page, size: 50, sort: "name", direction: "asc" },
                    )
                    .then((res) => ({
                      ...res,
                      // Only show CRM-linked users — drop any with a null/empty sourceCrm.
                      content: (res.content ?? []).filter((u) => u.sourceCrm && u.id !== currentUser?.id),
                    }))
                }
              />
            </div>
          )}

          </div>

          <div className="flex flex-col divide-y divide-bridge-border/40 border-t border-bridge-border/40 sm:border-l sm:border-t-0">
          <div className="pb-1">
            <SectionLabel>Properties</SectionLabel>
            <CalendarCheckboxList
              selectedIds={value.propertyIds}
              onChange={(ids) => onChange(applyDiaryCalendarFilterPatch(value, { propertyIds: ids }))}
              getId={(p: PropertySummary) => p.id}
              getLabel={(p: PropertySummary) => p.displayAddress || p.externalId || p.id}
              labelCache={labelCacheRef.current}
              searchPlaceholder="Search properties..."
              emptyLabel="No properties found"
              loadPage={(page, search) =>
                propertiesService.list(
                  { search: search || undefined, legalEntityId, branchId },
                  { page, size: 50, sort: "displayAddress", direction: "asc" },
                )
              }
            />
          </div>

          <div className="pb-1">
            <SectionLabel>Availability Calendars</SectionLabel>
            <CalendarCheckboxList
              selectedIds={value.availabilityPropertyIds}
              onChange={(ids) =>
                onChange(applyDiaryCalendarFilterPatch(value, { availabilityPropertyIds: ids }))
              }
              getId={(p: PropertySummary) => p.id}
              getLabel={(p: PropertySummary) => p.displayAddress || p.externalId || p.id}
              labelCache={labelCacheRef.current}
              searchPlaceholder="Search properties..."
              emptyLabel="No properties found"
              loadPage={(page, search) =>
                propertiesService.list(
                  { search: search || undefined, legalEntityId, branchId },
                  { page, size: 50, sort: "displayAddress", direction: "asc" },
                )
              }
            />
          </div>

          </div>
          </div>

          {!isDefault && (
            <div className="p-2">
              <button
                type="button"
                onClick={() => onChange(diaryCalendarFiltersDefault(isNegotiator))}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium text-bridge-muted hover:text-bridge-fg hover:bg-bridge-bg transition-colors"
              >
                <RotateCcw size={12} /> Reset to default
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
